import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl, {
	Map as MapboxMap,
	Marker as MapboxMarker,
	NavigationControl,
	Popup as MapboxPopup,
	supported as isMapboxSupported
} from 'mapbox-gl';
import _isFunction from 'lodash/isFunction';
import _isNumber from 'lodash/isNumber';
import { BringgMapProps, IdType, ImageUrl, MarkerData, Popup } from '../../types/maps.consts';
import { useMapboxLoadData } from '../../custom-hooks/mapbox/use-mapbox-load-data/use-mapbox-load-data';
import useMapResize from './utils/use-map-resize';
import './mapbox.scss';

export type MapboxImage = string;

export interface MarkerInstance {
	id: IdType;
	mapboxMarker?: MapboxMarker;
	mapboxPopup?: MapboxPopup;
	sourceId?: string;
}

export type MapboxProps = Omit<BringgMapProps<MapboxMap>, 'markers'> & {
	mapboxOptions: {
		style: string;
	};
	markers?: MapboxMarkerData[] | Promise<MapboxMarkerData[]>;
};

export interface MapData extends Pick<MapboxProps, 'apiKey' | 'zoom'> {
	staticMapUrl: string;
}

export type MapboxMarkerData = MarkerData & {
	popup?: Popup;
};

const Mapbox: React.FC<MapboxProps> = ({
	mapboxOptions,
	apiKey,
	zoom,
	zoomControl,
	center,
	clusterData,
	onLoaded,
	markers,
	polylines,
	className,
	children
}) => {
	const { style } = mapboxOptions;
	const [map, setMap] = useState<MapboxMap>();
	const markersMap: Map<IdType, MarkerInstance> = useMemo(() => new Map(), []);
	const imagesMap: Map<ImageUrl, MapboxImage> = useMemo(() => new Map(), []);
	const refContainer = useRef(null);
	useMapboxLoadData({
		map,
		markersMap,
		imagesMap,
		markers,
		polylines,
		clusterData
	});

	const { resizedContainerRef } = useMapResize(map);

	useEffect(() => {
		if (!isMapboxSupported()) return;

		if (mapboxgl.getRTLTextPluginStatus() === 'unavailable') {
			mapboxgl.setRTLTextPlugin(
				'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
				null,
				true
			);
		}

		const newMap = new MapboxMap({
			accessToken: apiKey,
			style,
			container: refContainer.current,
			center: center ? [center.lng, center.lat] : undefined,
			zoom
		});

		if (zoomControl) {
			newMap.addControl(
				new NavigationControl({
					showCompass: false,
					showZoom: true
				}),
				'top-left'
			);
		}

		newMap.on('load', () => {
			setMap(newMap);

			if (_isFunction(onLoaded)) {
				onLoaded(newMap);
			}
		});
	}, []);

	useEffect(() => {
		if (!map) {
			return;
		}
		map.setStyle(style);
	}, [style]);

	useEffect(() => {
		if (!map) {
			return;
		}

		if (_isNumber(zoom)) {
			map.setZoom(zoom);
		}
	}, [map, zoom]);

	useEffect(() => {
		if (!map) {
			return;
		}

		if (center) {
			map.panTo(center);
		}
	}, [map, center]);

	return (
		isMapboxSupported() && (
			<div className="mapbox-container" ref={resizedContainerRef}>
				<div ref={refContainer} className={className}>
					{children}
				</div>
			</div>
		)
	);
};

Mapbox.defaultProps = {
	zoomControl: true
};

export default Mapbox;
